import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, apiError, dateTimeOf } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Plus, Trash } from "@phosphor-icons/react";
import { isParent, isStaff } from "@/lib/roles";

export const FormsTab = ({ student, data, user }) => {
  const [subs, setSubs] = useState([]);
  const [imm, setImm] = useState([]);
  const [sigs, setSigs] = useState([]);
  const [forms, setForms] = useState([]);
  const [paper, setPaper] = useState({ form_id: "", received_from: "", relationship: "", received_date: "", note: "" });
  const [draft, setDraft] = useState({ vaccine: "", date_given: "", next_due: "", notes: "" });

  const load = useCallback(async () => {
    try {
      const reqs = [api.get(`/students/${student.id}/immunizations`), api.get("/signatures", { params: { student_id: student.id } })];
      if (!isParent(user)) reqs.push(api.get("/form-submissions"), api.get("/forms"));
      const [i, g, s, f] = await Promise.all(reqs);
      setImm(i.data);
      setSigs(g.data);
      if (s) setSubs(s.data.filter((x) => x.student_id === student.id || x.parent_email === student.parent_email || x.student_name === student.name));
      if (f) setForms(f.data);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  }, [student, user]);
  useEffect(() => { load(); }, [load]);

  const recordPaper = async () => {
    if (!paper.form_id) return toast.error("Choose the form");
    try {
      await api.post(`/forms/${paper.form_id}/paper-signature`, { ...paper, student_id: student.id });
      toast.success("Paper form recorded (marked as staff-recorded, not an e-signature)");
      setPaper({ form_id: "", received_from: "", relationship: "", received_date: "", note: "" }); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const addImm = async () => {
    if (!draft.vaccine.trim()) return toast.error("Enter the vaccine name");
    try { await api.post(`/students/${student.id}/immunizations`, draft); toast.success("Immunization recorded"); setDraft({ vaccine: "", date_given: "", next_due: "", notes: "" }); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const delImm = async (rid) => {
    try { await api.delete(`/students/${student.id}/immunizations/${rid}`); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const today = new Date().toISOString().slice(0, 10);

  return (
    <div className="grid lg:grid-cols-2 gap-4" data-testid="forms-tab">
      <div className="bg-white rounded-2xl border border-border p-6" data-testid="forms-status">
        <h2 className="font-display text-lg font-semibold">Required forms</h2>
        {data.missing_forms.length === 0 ? <p className="mt-3 text-sm text-emerald-700 font-semibold">All open forms are complete.</p> : (
          <ul className="mt-3 space-y-2 text-sm">
            {data.missing_forms.map((f) => (
              <li key={f.id} className="flex justify-between gap-2" data-testid={`missing-${f.id}`}>
                <span className="font-semibold">{f.title}</span>
                <Link to={`/forms/${f.id}`} className="text-[#3B82F6] font-bold">{isParent(user) ? "Complete" : "Open link"} →</Link>
              </li>
            ))}
          </ul>
        )}
        {!isParent(user) && (
          <>
            <h3 className="mt-6 font-bold text-sm">Submitted paperwork</h3>
            {subs.length === 0 && <p className="mt-2 text-sm text-muted-foreground">No submissions matched to this family yet.</p>}
            <ul className="mt-2 divide-y divide-border text-sm">
              {subs.map((s) => (
                <li key={s.id} className="py-2 flex justify-between gap-2" data-testid={`submission-${s.id}`}>
                  <span><strong>{s.form_title}</strong><span className="block text-xs text-muted-foreground">{s.parent_name || s.parent_email} · {s.submitted_at?.slice(0, 10)}{s.signature ? " · signed" : ""}</span></span>
                  <span className={`text-xs font-bold ${s.status === "Reviewed" ? "text-emerald-600" : "text-amber-600"}`}>{s.status}</span>
                </li>
              ))}
            </ul>
            <Link to="/admin/forms" className="mt-4 inline-block text-sm font-bold text-[#3B82F6]">Manage forms →</Link>
          </>
        )}
        <h3 className="mt-6 font-bold text-sm">Signed documents</h3>
        {sigs.length === 0 && <p className="mt-2 text-sm text-muted-foreground">No signatures on file for this child.</p>}
        <ul className="mt-2 divide-y divide-border text-sm" data-testid="signature-list">
          {sigs.map((g) => (
            <li key={g.id} className="py-2 flex justify-between gap-2 items-center" data-testid={`signature-${g.id}`}>
              <span><strong>{g.document_title}</strong> <span className="text-xs text-muted-foreground">v{g.document_version}</span>
                <span className="block text-xs text-muted-foreground">{g.kind === "paper" ? `Paper copy from ${g.signer_name}, recorded by ${g.recorded_by}` : `E-signed by ${g.signer_name} (${g.relationship})`} · {dateTimeOf(g.signed_at)}</span></span>
              <span className="flex items-center gap-2">
                <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${g.status === "valid" ? (g.kind === "paper" ? "bg-slate-100 text-slate-600" : "bg-emerald-50 text-emerald-700") : "bg-amber-50 text-amber-700"}`}>{g.kind === "paper" ? "Paper" : g.status === "valid" ? "Valid" : "Superseded"}</span>
                <Link to={`${isParent(user) ? "/portal" : "/admin"}/signed/${g.id}`} className="text-[#3B82F6] font-bold" data-testid={`open-signature-${g.id}`}>View</Link>
              </span>
            </li>
          ))}
        </ul>
        {isStaff(user) && (
          <details className="mt-4 rounded-2xl border border-border p-3" data-testid="paper-record">
            <summary className="text-sm font-bold cursor-pointer">Record a paper form received</summary>
            <p className="mt-2 text-xs text-muted-foreground">Recorded as a staff-received paper signature — never as an electronic signature by the parent.</p>
            <div className="mt-3 grid sm:grid-cols-2 gap-2">
              <select value={paper.form_id} onChange={(e) => setPaper({ ...paper, form_id: e.target.value })} className="h-10 rounded-xl border border-input px-3 text-sm sm:col-span-2" data-testid="paper-form">
                <option value="">Choose the form…</option>{forms.map((f) => <option key={f.id} value={f.id}>{f.title} (v{f.version || 1})</option>)}
              </select>
              <Input placeholder="Signed by (name on paper)" value={paper.received_from} onChange={(e) => setPaper({ ...paper, received_from: e.target.value })} className="h-10 rounded-xl" data-testid="paper-from" />
              <Input placeholder="Relationship" value={paper.relationship} onChange={(e) => setPaper({ ...paper, relationship: e.target.value })} className="h-10 rounded-xl" data-testid="paper-relationship" />
              <Input type="date" value={paper.received_date} onChange={(e) => setPaper({ ...paper, received_date: e.target.value })} className="h-10 rounded-xl" data-testid="paper-date" />
              <Input placeholder="Note (optional)" value={paper.note} onChange={(e) => setPaper({ ...paper, note: e.target.value })} className="h-10 rounded-xl" data-testid="paper-note" />
              <Button onClick={recordPaper} variant="outline" className="sm:col-span-2 rounded-full font-bold" data-testid="paper-save">Record paper form</Button>
            </div>
          </details>
        )}
        <h3 className="mt-6 font-bold text-sm">Upcoming document dates</h3>
        {data.upcoming_documents.length === 0 && <p className="mt-2 text-sm text-muted-foreground">Nothing due in the next 60 days.</p>}
        <ul className="mt-2 space-y-1 text-sm">
          {data.upcoming_documents.map((d, i) => <li key={`${d.type}-${i}`} className={d.overdue ? "text-red-600 font-semibold" : ""}>{d.type} · {d.label} — {d.overdue ? "overdue" : "due"} {d.due}</li>)}
        </ul>
      </div>

      <div className="bg-white rounded-2xl border border-border p-6" data-testid="immunizations-panel">
        <h2 className="font-display text-lg font-semibold">Immunization records</h2>
        {imm.length === 0 && <p className="mt-3 text-sm text-muted-foreground">No immunizations on file yet.</p>}
        <div className="mt-3 space-y-2">
          {imm.map((r) => (
            <div key={r.id} className="rounded-2xl border border-border p-3 flex flex-wrap gap-2 items-center text-sm" data-testid={`immunization-row-${r.id}`}>
              <div className="flex-1 min-w-0"><p className="font-bold">{r.vaccine}</p><p className="text-xs text-muted-foreground">Given {r.date_given || "—"}{r.next_due ? ` · next due ${r.next_due}` : ""}</p></div>
              {r.next_due && r.next_due < today && <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-red-50 text-red-600">overdue</span>}
              {isStaff(user) && <Button size="sm" variant="ghost" className="rounded-full text-[#EF4444]" onClick={() => delImm(r.id)} data-testid={`delete-immunization-${r.id}`}><Trash size={14} /></Button>}
            </div>
          ))}
        </div>
        {isStaff(user) && (
          <div className="mt-4 border-t border-border pt-4 grid sm:grid-cols-2 gap-2 items-end">
            <div className="sm:col-span-2"><Label className="text-xs font-bold">Vaccine</Label><Input value={draft.vaccine} onChange={(e) => setDraft({ ...draft, vaccine: e.target.value })} placeholder="DTaP, MMR, Polio…" className="mt-1 h-10 rounded-xl" data-testid="immunization-vaccine" /></div>
            <div><Label className="text-xs font-bold">Date given</Label><Input type="date" value={draft.date_given} onChange={(e) => setDraft({ ...draft, date_given: e.target.value })} className="mt-1 h-10 rounded-xl" data-testid="immunization-date" /></div>
            <div><Label className="text-xs font-bold">Next due</Label><Input type="date" value={draft.next_due} onChange={(e) => setDraft({ ...draft, next_due: e.target.value })} className="mt-1 h-10 rounded-xl" data-testid="immunization-next-due" /></div>
            <Button onClick={addImm} className="sm:col-span-2 rounded-full bg-[#3B82F6] hover:bg-[#2563EB] font-bold" data-testid="immunization-add-btn"><Plus size={15} className="mr-1" /> Add record</Button>
          </div>
        )}
      </div>
    </div>
  );
};
